export function TorontoMarket() {
  const stats = [
    { value: '$185', label: 'Average Nightly Rate', trend: '+8% YoY' },
    { value: '68%', label: 'Average Occupancy', trend: 'Stable' },
    { value: '6,500+', label: 'Registered STRs', trend: 'Regulated Market' },
    { value: '$46K', label: 'Avg Annual Revenue', trend: 'Top Performers' }
  ];

  const neighborhoods = [
    { name: 'Entertainment District', demand: 'Very High', type: 'Business & Tourism' },
    { name: 'Liberty Village', demand: 'Very High', type: 'Young Professionals' },
    { name: 'Downtown Core', demand: 'Very High', type: 'Business & Tourism' },
    { name: 'Queen West', demand: 'High', type: 'Arts & Entertainment' },
    { name: 'Yorkville', demand: 'Very High', type: 'Luxury & Shopping' },
    { name: 'Distillery District', demand: 'High', type: 'Tourism & Events' }
  ];

  return (
    <section className="py-24 lg:py-32 bg-[#F8F8F8]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <span className="inline-block bg-[#882629]/10 text-[#882629] px-4 py-2 rounded-full text-sm font-medium mb-4">
            Market Insights
          </span>
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            Toronto STR Market Performance
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            As Canada's largest city and economic hub, Toronto offers exceptional STR potential with year-round demand from business travelers, tourists, and event attendees.
          </p>
        </div>

        <div className="grid md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-8 text-center shadow-sm hover:shadow-lg transition-all"
            >
              <div className="text-4xl font-bold text-[#882629] mb-2">{stat.value}</div>
              <p className="text-[#505A5A] font-medium mb-1">{stat.label}</p>
              <span className="text-sm text-green-600 font-medium">{stat.trend}</span>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="text-2xl font-semibold text-[#505A5A] mb-6">
              Top Neighborhoods for STR
            </h3>
            <div className="space-y-4">
              {neighborhoods.map((area, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-5 shadow-sm flex items-center justify-between"
                >
                  <div>
                    <h4 className="font-medium text-[#505A5A]">{area.name}</h4>
                    <span className="text-sm text-[#505A5A]/60">{area.type}</span>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      area.demand === 'Very High'
                        ? 'bg-green-100 text-green-700'
                        : area.demand === 'High'
                        ? 'bg-[#882629]/10 text-[#882629]'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {area.demand} Demand
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <h3 className="text-2xl font-semibold text-[#505A5A] mb-6">
              Why Toronto for STR?
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-xl text-[#882629] mt-0.5"></i>
                <div>
                  <span className="font-medium text-[#505A5A]">Year-Round Demand</span>
                  <p className="text-sm text-[#505A5A]/60">
                    Business travel, festivals, sports events, and tourism drive consistent bookings throughout the year
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-xl text-[#882629] mt-0.5"></i>
                <div>
                  <span className="font-medium text-[#505A5A]">Premium Rates</span>
                  <p className="text-sm text-[#505A5A]/60">
                    Higher nightly rates compared to most Canadian cities, especially in Entertainment District and Liberty Village
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-xl text-[#882629] mt-0.5"></i>
                <div>
                  <span className="font-medium text-[#505A5A]">Major Events</span>
                  <p className="text-sm text-[#505A5A]/60">
                    TIFF (Toronto International Film Festival), Caribana, and sports playoffs create peak demand periods
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-xl text-[#882629] mt-0.5"></i>
                <div>
                  <span className="font-medium text-[#505A5A]">Corporate Travel</span>
                  <p className="text-sm text-[#505A5A]/60">
                    Canada's financial capital brings steady business traveler demand, particularly in downtown areas
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 bg-gradient-to-r from-[#882629]/5 to-[#882629]/10 rounded-2xl p-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#882629]/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-calendar-event-line text-2xl text-[#882629]"></i>
            </div>
            <div>
              <h4 className="text-xl font-semibold text-[#505A5A] mb-2">Peak Demand Periods</h4>
              <p className="text-[#505A5A]/70 mb-4">
                Toronto's STR market sees consistent year-round demand with notable peaks during major events:
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white/60 rounded-lg p-4">
                  <div className="font-semibold text-[#882629] mb-1">TIFF (September)</div>
                  <p className="text-sm text-[#505A5A]/70">Toronto International Film Festival drives premium rates</p>
                </div>
                <div className="bg-white/60 rounded-lg p-4">
                  <div className="font-semibold text-[#882629] mb-1">Summer (June-Aug)</div>
                  <p className="text-sm text-[#505A5A]/70">Peak tourism season with festivals and events</p>
                </div>
                <div className="bg-white/60 rounded-lg p-4">
                  <div className="font-semibold text-[#882629] mb-1">Business Travel</div>
                  <p className="text-sm text-[#505A5A]/70">Steady weekday demand year-round in downtown</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
